"use client";

import { useEffect, useMemo, useState } from "react";

import type { ProgramDetail, ProgramListItem } from "@/lib/api/api-client";
import { readJourneyProgress } from "@/lib/library/journey-glance";
import {
  deriveProgramState,
  unlockedProgramIds,
  type PieceProgressLike,
  type ProgramState,
} from "./program-state";

/**
 * Rang d'étape, dupliqué ici pour rester local à la comparaison. La table
 * complète vit dans `program-state.ts`, qui est le module qui décide.
 */
const STAGE_ORDER = ["S0", "S1a", "S1b", "S2a", "S2b", "S3", "S4", "S5", "DONE"];

/**
 * Lit `localStorage` dans un effet et jamais pendant le rendu : le serveur ne
 * connaît pas le stockage, et le lire au rendu client produirait une
 * hydratation divergente.
 */
function useLocalStorage(): Storage | null {
  return useMemo(
    () => (typeof window === "undefined" ? null : window.localStorage),
    [],
  );
}

/** Où en est ce parcours, jalon par jalon. */
export function useProgramProgress(program: ProgramDetail | null): ProgramState | null {
  const storage = useLocalStorage();
  const [state, setState] = useState<ProgramState | null>(null);

  useEffect(() => {
    if (storage === null || program === null) {
      return;
    }

    const pieces = new Map<string, PieceProgressLike>();

    for (const milestone of program.milestones) {
      if (milestone.kind !== "piece" || milestone.midiFileId === null) {
        continue;
      }

      const progress = readJourneyProgress(storage, milestone.midiFileId);

      if (progress !== null) {
        pieces.set(milestone.midiFileId, progress);
      }
    }

    setState(deriveProgramState(program, { pieces, exercises: new Map() }));
  }, [storage, program]);

  return state;
}

/**
 * Les parcours ouverts, c'est-à-dire ceux dont tous les prérequis sont finis.
 *
 * Rend `null` tant que le stockage n'a pas été lu, pour que l'écran n'affiche
 * pas un verrou pendant une frame avant de le retirer.
 */
export function useUnlockedPrograms(
  programs: readonly ProgramListItem[],
): Set<string> | null {
  const storage = useLocalStorage();
  const [unlocked, setUnlocked] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (storage === null || programs.length === 0) {
      return;
    }

    const completed = new Set<string>();

    for (const program of programs) {
      const gates = program.gates.filter((g) => g.midiFileId !== null);

      if (gates.length === 0) {
        continue;
      }

      const allCrossed = gates.every((gate) => {
        const progress = readJourneyProgress(storage, gate.midiFileId!);

        if (progress === null || progress.phrases.length === 0) {
          return false;
        }

        // Pas de porte déclarée : le jalon se franchit dès qu'il s'ouvre.
        if (gate.stage === null) {
          return true;
        }

        const target = STAGE_ORDER.indexOf(gate.stage);

        return progress.phrases.every(
          (phrase) => STAGE_ORDER.indexOf(phrase.etatMax) >= target,
        );
      });

      if (allCrossed) {
        completed.add(program.vpfId);
      }
    }

    setUnlocked(unlockedProgramIds(programs, completed));
  }, [storage, programs]);

  return unlocked;
}
